import React, { useCallback, useContext, useMemo, useState } from 'react';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { useLoader } from '../../api/useLoader';
import {
  BrukerrollerDto,
  MinimalPraksisstedDto,
  PersonDto,
  PraksisplassTilFordelingDto,
  PraksisstedTilFordelingDto,
  StudentfordelingDto,
  StudentfordelingMedValgDto,
  StudentfordelingPutDto,
} from '../../api/generated/studiested-generated-api';
import * as css from './fordeling.module.scss';
import Button from '../../components/button';
import translations from './fordeling.i18n';
import { useIntl } from 'react-intl';
import Spinner from '../../components/spinner/spinner';
import { UhaUserContext } from '../../user/UhaUserStore';
import Icon, { IconType } from '../../components/icon';
import IconButton from '@mui/material/IconButton';
import { NavLink } from 'react-router-dom';
import { ConfirmationDialog } from '../../components/dialog';
import Dropdown, { DropdownOption } from '../../components/input/dropdown';
import { NumberInput } from '../../components/input';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import useUnsavedChangesPrompt from '../../hooks/useUnsavedChangesPrompt';
import {
  OnStedHoverCard,
  StedType,
} from '../../components/hierarchy/onhoverhierarchyinfo';
import LocalAlert, {
  AlertType,
} from '../../components/notifications/localAlert';
import classNames from 'classnames';
import { useActivityContextUHA } from '../activitycontexts';
import FordelingDownloadButtons from './FordelingDownloadButtons';
import { compareByMultiple, CompareFunc } from '../../util/compareutils';
import { useFormatMessageFor } from '../../i18n/translation';
import useAddErrorNotificationCallback from '../../hooks/useAddErrorNotificationCallback';
import FeilmeldingerFraFS, { groupByPraksissted } from './feilmeldingerFraFS';
import PanelHeader from '../../components/panelheader/panelHeader';

const notSelected = 'notSelected';
export const noPriority = 99;

const getTableScrollTopFromSessionStorage = (activityId: string): number =>
  parseInt(
    sessionStorage.getItem(`scrollFordelingTableInActivity-${activityId}`) ||
      '0',
  );

const putTableScrollTopInSessionStorage = (activityId: string, num: number) =>
  sessionStorage.setItem(
    `scrollFordelingTableInActivity-${activityId}`,
    num.toString(),
  );

export const getFullName = (person: PersonDto) =>
  `${person.etternavn}, ${person.fornavn}`;

export const getMuligePraksisplasser = (
  muligePraksissteder: PraksisstedTilFordelingDto[],
) =>
  muligePraksissteder.flatMap(
    (praksissted) => praksissted.muligePraksisplasser,
  );

const getPriority = (
  praksisplass: PraksisplassTilFordelingDto,
  onskeliste?: MinimalPraksisstedDto[],
) => {
  const priorities = (onskeliste || []).flatMap((onske, i) =>
    onske.id === praksisplass.praksissted.id ? i + 1 : [],
  );
  return priorities.length > 0 ? priorities[0] : noPriority;
};

export const getSortedMuligePraksisplasser = (
  muligePraksisplasser: PraksisplassTilFordelingDto[],
  studentfordeling: FordelingRow,
) => {
  return muligePraksisplasser
    .map((praksisplass) => ({
      praksisplass,
      priority: getPriority(praksisplass, studentfordeling.onskeliste),
      alert: praksisplass.hasValideringsfeil,
    }))
    .sort((a, b) => a.priority - b.priority);
};

export const studentFordelingToFordelingEdit = (
  studentFordelinger: StudentfordelingDto[],
): FordelingEdit => {
  const fordelingMap: FordelingEdit = {};
  for (const studentFordeling of studentFordelinger) {
    const praksisplassId = studentFordeling.praksisplass
      ? studentFordeling.praksisplass.praksissted.id
      : '';
    const underviserIds = studentFordeling.undervisere
      ? studentFordeling.undervisere.map((u) => u.id)
      : [];
    const veilederIds = studentFordeling.veiledere
      ? studentFordeling.veiledere.map((v) => v.id)
      : [];
    fordelingMap[studentFordeling.student.id] = {
      student: studentFordeling.student,
      praksissted: {
        id: praksisplassId,
        savedId: praksisplassId,
        autoAssigned: false,
      },
      undervisere: {
        ids: underviserIds,
        savedIds: underviserIds,
      },
      veiledere: {
        ids: veilederIds,
        savedIds: veilederIds,
      },
      onskeliste: studentFordeling.onskeliste || [],
      hasValideringsfeil: studentFordeling.hasValideringsfeil,
    };
  }
  return fordelingMap;
};

export interface FordelingRow {
  student: PersonDto;
  praksissted: {
    id: string;
    autoAssigned?: boolean;
    savedId: string;
  };
  undervisere: {
    ids: string[];
    savedIds: string[];
  };
  veiledere: {
    ids: string[];
    savedIds: string[];
  };
  onskeliste: MinimalPraksisstedDto[];
  hasValideringsfeil: boolean;
}

interface FordelingEdit {
  [studentId: string]: FordelingRow;
}

const Fordeling = () => {
  const { praksisaktivitet, reloadPraksisaktivitet } = useActivityContextUHA();
  const { id: aktivitetsId } = praksisaktivitet;

  const { praksiskalenderApi } = useContext(StudiestedApiContext);

  const { data, reload } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdFordelingGet({
        pathParams: { id: aktivitetsId },
      }),
    [aktivitetsId],
  );

  if (!data) {
    return <Spinner />;
  }

  return (
    <FordelingContent
      data={data}
      reloadFordeling={reload}
      reloadAktivitet={reloadPraksisaktivitet}
    />
  );
};

const praksisplassIsChanged = (fordeling: FordelingRow) =>
  fordeling.praksissted.id !== fordeling.praksissted.savedId;

const underviserIsChanged = (fordeling: FordelingRow) =>
  fordeling.undervisere.ids.some(
    (id) => !fordeling.undervisere.savedIds.includes(id),
  );

const veilederIsChanged = (fordeling: FordelingRow) =>
  fordeling.veiledere.ids.some(
    (id) => !fordeling.veiledere.savedIds.includes(id),
  );

const fordelingIsChanged = (fordeling: FordelingRow) =>
  praksisplassIsChanged(fordeling) ||
  underviserIsChanged(fordeling) ||
  veilederIsChanged(fordeling);

interface FordelingContentProps {
  data: StudentfordelingMedValgDto;
  reloadFordeling: () => void;
  reloadAktivitet: () => void;
}

type UserRightsType = {
  isAdmin: boolean;
  canWrite: boolean;
};

type BrukerrolleToUserRights = { [key in BrukerrollerDto]: UserRightsType };

const UserRights: BrukerrolleToUserRights = {
  [BrukerrollerDto.Administrator]: { isAdmin: true, canWrite: true },
  [BrukerrollerDto.Skriverettighet]: { isAdmin: false, canWrite: true },
  [BrukerrollerDto.Leserettighet]: { isAdmin: false, canWrite: false },
  [BrukerrollerDto.Registrererettighet]: { isAdmin: false, canWrite: true },
};

const getUserRights = (hoeyesteRettighet: BrukerrollerDto): UserRightsType => {
  switch (hoeyesteRettighet) {
    case BrukerrollerDto.Administrator:
      return UserRights[BrukerrollerDto.Administrator];
    case BrukerrollerDto.Skriverettighet:
      return UserRights[BrukerrollerDto.Skriverettighet];
    case BrukerrollerDto.Leserettighet:
      return UserRights[BrukerrollerDto.Leserettighet];
    case BrukerrollerDto.Registrererettighet:
      return UserRights[BrukerrollerDto.Registrererettighet];
  }
};

/** IMPORTANT - The positioning of the keys determines priority */
type ComparableStudentObject = {
  etternavn: string;
  fornavn: string;
  id: string;
};

const getComparableStudentObject = (
  student: PersonDto,
): ComparableStudentObject => ({
  etternavn: student.etternavn,
  fornavn: student.fornavn,
  id: student.id,
});

export const compareStudents = (
  studentA: PersonDto,
  studentB: PersonDto,
  compare: CompareFunc,
): number =>
  compareByMultiple(
    getComparableStudentObject(studentA),
    getComparableStudentObject(studentB),
    compare,
  );

export const navnSearch = (
  fornavn: string,
  etternavn: string,
  search: string,
) => {
  const terms = search.toLowerCase().split(' ');

  return terms.every(
    (term) =>
      fornavn.toLowerCase().includes(term) ||
      etternavn.toLowerCase().includes(term),
  );
};

const FordelingContent = ({
  data,
  reloadFordeling,
  reloadAktivitet,
}: FordelingContentProps) => {
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { uhaUser: bruker } = useContext(UhaUserContext);
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const { praksisaktivitet } = useActivityContextUHA();
  const {
    id: aktivitetsId,
    publisertTilFs,
    publisertTilPs,
    fsStatus,
  } = praksisaktivitet;

  const errorNotificationCallback = useAddErrorNotificationCallback();
  const { locale } = useIntl();
  const labels = useFormatMessageFor(translations);

  const [fordelingEdit, setFordelingEdit] = useState<FordelingEdit>(
    studentFordelingToFordelingEdit(data.studentfordeling),
  );

  const [publisertTilFsState, setPublisertTilFs] = useState(publisertTilFs);
  const [publisertTilPsState, setPublisertTilPs] = useState(publisertTilPs);
  const [sendResultConfirmation, setResultConfirmation] = useState(false);
  const [sharedWithPSConfirmation, setSharedwithPSConfirmation] =
    useState(false);
  const [visLocalAlert, setVisLocalAlert] = useState<boolean>(true);
  const [activeCol, setActiveCol] = useState<number>(-1);
  const [search, setSearch] = useState<string[]>(['', '', '', '']);
  const [sortedFordelinger, setSortedFordelinger] =
    useState<[string, FordelingRow][]>();

  const { isAdmin, canWrite } = useMemo(
    () =>
      getUserRights(bruker?.hoeyesteRettighet || BrukerrollerDto.Leserettighet),
    [bruker],
  );

  const capacityIsReached = useMemo(() => {
    const capacity = data.muligePraksissteder
      .flatMap((sted) =>
        sted.muligePraksisplasser.map((plass) => plass.kapasitet),
      )
      .reduce((acc, num) => acc + num, 0);
    const usedCapacity = data.studentfordeling.filter(
      (fordeling) => fordeling.praksisplass,
    ).length;

    return capacity === usedCapacity;
  }, [data.studentfordeling, data.muligePraksissteder]);

  const feilmeldingerPerPraksissted = groupByPraksissted(fsStatus.feil);

  const hasFeilmeldingerFraFsToPresent =
    Object.keys(feilmeldingerPerPraksissted).length > 0;

  const muligePraksisplasser = useMemo(
    () => getMuligePraksisplasser(data.muligePraksissteder),
    [data.muligePraksissteder],
  );

  // Updates fordelingEdit and returns which fields were changed
  const updateFordelingEdit = useCallback(
    (
      fordeling: FordelingRow,
      underviserIds: string[],
      praksisstedId: string,
      veilederIds: string[],
      wasSaved: boolean,
    ) => {
      const praksisplassId = praksisstedId === notSelected ? '' : praksisstedId;
      const veilederIdsBasedOnMulige = veilederIds.filter((id) =>
        (
          muligePraksisplasser
            .find(
              (praksisplass) => praksisplass.praksissted.id === praksisplassId,
            )
            ?.muligeVeiledere.map((veileder) => veileder.id) || []
        ).includes(id),
      );

      setFordelingEdit((prevState) => ({
        ...prevState,
        [fordeling.student.id]: {
          ...fordeling,
          praksissted: {
            id: praksisplassId,
            savedId: wasSaved ? praksisplassId : fordeling.praksissted.savedId,
          },
          undervisere: {
            ids: underviserIds,
            savedIds: wasSaved ? underviserIds : fordeling.undervisere.savedIds,
          },
          veiledere: {
            ids: veilederIdsBasedOnMulige,
            savedIds: wasSaved
              ? veilederIdsBasedOnMulige
              : fordeling.veiledere.savedIds,
          },
        },
      }));
    },
    [muligePraksisplasser],
  );

  const [hasUnsavedChanges, hasUnsavedEmptyPraksisplasser] = useMemo(() => {
    if (!fordelingEdit) {
      return [false, false];
    }

    let unsavedChanges = false;
    let unsavedEmptyPraksisplasser = false;

    for (const fordelingRow of Object.values(fordelingEdit)) {
      if (unsavedChanges && unsavedEmptyPraksisplasser) break;

      if (!unsavedChanges && fordelingIsChanged(fordelingRow)) {
        unsavedChanges = true;
      }

      if (
        !unsavedEmptyPraksisplasser &&
        fordelingRow.praksissted.id === '' &&
        fordelingRow.praksissted.id !== fordelingRow.praksissted.savedId
      ) {
        unsavedEmptyPraksisplasser = true;
      }
    }

    return [unsavedChanges, unsavedEmptyPraksisplasser];
  }, [fordelingEdit]);

  const saveChangedRows = (publiserTilPsa: boolean, publiserTilFs: boolean) => {
    const fordelinger: StudentfordelingPutDto[] = Object.entries(fordelingEdit)
      .filter(([, fordeling]) => fordelingIsChanged(fordeling))
      .map(([studentId, fordeling]) => ({
        studentId,
        underviserIder: fordeling.undervisere.ids,
        praksisplassId: fordeling.praksissted.id || undefined,
        veilederIder: fordeling.veiledere.ids,
      }));
    saveFordelinger(
      fordelinger,
      reloadFordeling,
      reloadAktivitet,
      publiserTilPsa,
      publiserTilFs,
    );
  };

  const saveFordelinger = useCallback(
    (
      fordelinger: StudentfordelingPutDto[],
      reloadFordelinger: () => void,
      reloadPraksisaktivitet: () => void,
      publiserTilPsa: boolean,
      publiserTilFs: boolean,
    ) => {
      let successMessage: string;
      if (publiserTilPsa) {
        successMessage = labels.publisertTilPsa;
      } else {
        successMessage =
          publiserTilFs && !publisertTilPsState
            ? labels.publisertTilStudenter
            : labels.lagret;
      }

      praksiskalenderApi
        .praksisaktivitetIdFordelingPut({
          pathParams: { id: aktivitetsId! }, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          queryParams: {
            publiserTilFs: publiserTilFs,
            publiserTilPsa: publiserTilPsa,
          },
          studentfordelingPutDto: fordelinger,
        })
        .then(() => {
          addSuccessNotification(successMessage);

          const newFordelingEdit = { ...fordelingEdit };
          for (const fordeling of fordelinger) {
            delete newFordelingEdit[fordeling.studentId];
          }
          setFordelingEdit(newFordelingEdit);

          if (publiserTilFs) {
            setPublisertTilFs(true);
            reloadPraksisaktivitet();
          }
          if (publiserTilPsa) {
            setPublisertTilPs(true);
            reloadPraksisaktivitet();
          }
          reloadFordelinger();
        })
        .catch(errorNotificationCallback);
    },
    [
      addSuccessNotification,
      errorNotificationCallback,
      aktivitetsId,
      fordelingEdit,
      labels.lagret,
      labels.publisertTilPsa,
      labels.publisertTilStudenter,
      praksiskalenderApi,
      publisertTilPsState,
      setPublisertTilFs,
      setPublisertTilPs,
    ],
  );

  const saveSingleRow = useCallback(
    (studentId: string) => {
      const studentEdit = fordelingEdit[studentId];
      const fordeling = {
        studentId,
        underviserIder: studentEdit.undervisere.ids,
        praksisplassId: studentEdit.praksissted.id || undefined,
        veilederIder: studentEdit.veiledere.ids,
      };

      const reloadFordelinger = () =>
        updateFordelingEdit(
          studentEdit,
          studentEdit.undervisere.ids,
          studentEdit.praksissted.id,
          studentEdit.veiledere.ids,
          true,
        );
      saveFordelinger(
        [fordeling],
        reloadFordelinger,
        () => {
          /* no need to reload praksisaktivitet when saving single row */
        },
        false,
        false,
      );
    },
    [fordelingEdit, saveFordelinger, updateFordelingEdit],
  );

  const sendResultConfirmationCloseHandler = (confirmed: boolean) => {
    setResultConfirmation(false);

    if (confirmed) {
      saveChangedRows(false, true);
    }
  };

  const shareResultConfirmationCloseHandler = (confirmed: boolean) => {
    setSharedwithPSConfirmation(false);

    if (confirmed) {
      saveChangedRows(true, false);
    }
  };

  const autoAssign = () => {
    praksiskalenderApi
      .praksisaktivitetIdAutomatiskfordelingGet({
        pathParams: { id: aktivitetsId },
      })
      .then((fordelinger) => {
        const edits = { ...fordelingEdit };

        fordelinger.forEach((fordeling) => {
          const praksisplassAutoAssigned = !!fordeling.praksisplass;
          const praksisplassId = praksisplassAutoAssigned
            ? fordeling.praksisplass!.praksissted.id
            : '';
          const student = fordeling.student;
          const edit = edits[student.id];

          // Assuming the backend will only assign praksissted
          edits[student.id].praksissted = {
            id: praksisplassId,
            autoAssigned: praksisplassAutoAssigned,
            savedId: edit.praksissted.savedId,
          };
        });

        setFordelingEdit(edits);
      })
      .catch(errorNotificationCallback);
  };

  const tableHeaders = useMemo(
    () => [
      { title: labels.student },
      { title: labels.underviser },
      { title: labels.praksissted },
      { title: labels.veileder },
      { title: labels.checkCircles },
    ],
    [labels],
  );

  const getNumStudentsWhere = useCallback(
    (shouldIncrement: (fordeling: FordelingRow) => boolean): number =>
      Object.values(fordelingEdit).filter((fordeling) =>
        shouldIncrement(fordeling),
      ).length,
    [fordelingEdit],
  );

  const numStudentsWithUnsavedPraksisplass = useMemo(
    (): number =>
      getNumStudentsWhere((fordeling) => !fordeling.praksissted.savedId),
    [getNumStudentsWhere],
  );

  const numStudentsOnPraksissteder = useMemo(() => {
    // Student placements fetched from the backend
    // We only care about placements to the given prakisplassId
    // and reduce to a map (studentId: praksispassId)
    const fordelingerBackend = Object.entries(fordelingEdit)
      .map(([studentId, edit]) => ({
        studentId,
        praksisplassId: edit.praksissted ? edit.praksissted.id : '',
      }))
      .reduce((map: { [index: string]: string }, obj) => {
        map[obj.studentId] = obj.praksisplassId;
        return map;
      }, {});

    // Student placement edits made in the frontend
    // We reduce to a map and this time we keep all placements as they might
    // override the ones from the backend
    const edits = Object.entries(fordelingEdit)
      .map((edit) => ({
        studentId: edit[0],
        praksisplassId: edit[1].praksissted.id,
      }))
      .reduce((map: { [index: string]: string }, obj) => {
        map[obj.studentId] = obj.praksisplassId;
        return map;
      }, {});

    // Merge the placements from the backend and the edits. Where studentIds are the same
    // the placement edit will replace the one from the backend, and then count how many are
    // now matching the given praksisplassId
    const fordelingerCurrent = { ...fordelingerBackend, ...edits };

    return Object.values(fordelingerCurrent).reduce(
      (acc: { [praksisplassId: string]: number }, praksisplassId) => ({
        ...acc,
        [praksisplassId]:
          (acc.hasOwnProperty(praksisplassId) ? acc[praksisplassId] : 0) + 1,
      }),
      {},
    );
  }, [fordelingEdit]);

  const getPraksisstedItems = useCallback(
    (studentfordeling: FordelingRow) =>
      getSortedMuligePraksisplasser(muligePraksisplasser, studentfordeling).map(
        (praksisplassMedInfo) => {
          const { praksisplass, priority, alert } = praksisplassMedInfo;
          const numStudents =
            numStudentsOnPraksissteder[praksisplass.praksissted.id] || 0;
          const hasPriority = priority !== noPriority ? `${priority}. ` : '';
          const praksisstedNavnAndKapasitet = `${hasPriority}${praksisplass.praksissted.navn} (${numStudents}/${praksisplass.kapasitet})`;

          return {
            id: praksisplass.praksissted.id,
            navn: praksisstedNavnAndKapasitet,
            deaktivert: numStudents >= praksisplass.kapasitet || alert,
            tooltipText: praksisstedNavnAndKapasitet,
            onHoverType: StedType.PRAKSISSTED,
            alert: alert,
          };
        },
      ),
    [muligePraksisplasser, numStudentsOnPraksissteder],
  );

  const filteredAndSortedFordelinger = useMemo(() => {
    return (sortedFordelinger || Object.entries(fordelingEdit)).filter(
      ([, fordeling]) => {
        const filterStudent =
          search[0].length === 0 ||
          navnSearch(
            fordeling.student.fornavn,
            fordeling.student.etternavn,
            search[0],
          );

        const filterUnderviser =
          search[1].length === 0 ||
          fordeling.undervisere.ids.some((id) => {
            const underviser = data.muligeUndervisere.find((u) => u.id === id);
            return (
              underviser &&
              navnSearch(underviser.fornavn, underviser.etternavn, search[1])
            );
          });

        const praksissted = data.muligePraksissteder
          .flatMap((praksisstedTopp) => praksisstedTopp.muligePraksisplasser)
          .find((place) => place.praksissted.id === fordeling.praksissted.id);
        const filterPlassering =
          search[2].length === 0 ||
          (praksissted &&
            praksissted.praksissted.navn.toLowerCase().indexOf(search[2]) > -1);

        const filterVeileder =
          search[3].length === 0 ||
          fordeling.veiledere.ids.some((id) => {
            const veiledere = data.muligePraksissteder.flatMap((place) =>
              place.muligePraksisplasser.flatMap((it) => it.muligeVeiledere),
            );

            return veiledere.find(
              (veileder) =>
                veileder &&
                veileder.id === id &&
                navnSearch(veileder.fornavn, veileder.etternavn, search[3]),
            );
          });

        return (
          filterStudent &&
          filterUnderviser &&
          filterPlassering &&
          filterVeileder
        );
      },
    );
  }, [fordelingEdit, sortedFordelinger, search, data]);

  const fordelingRows = useMemo(
    () =>
      filteredAndSortedFordelinger.map(([studentId, fordeling]) => {
        const edit = fordelingEdit[studentId];
        const {
          praksissted: { id: praksisstedId },
        } = fordeling;

        const feilVedPubliseringOnPraksissted =
          feilmeldingerPerPraksissted.hasOwnProperty(praksisstedId) &&
          feilmeldingerPerPraksissted[praksisstedId].length > 0;

        return (
          <FordelingTableRow
            key={studentId}
            fordeling={fordeling}
            muligePraksissteder={data.muligePraksissteder}
            praksisstedItems={getPraksisstedItems(fordeling)}
            muligeUndervisere={data.muligeUndervisere}
            updateFordelingEdit={updateFordelingEdit}
            praksisplassAutoAssigned={
              edit ? !!edit.praksissted.autoAssigned : false
            }
            saveSingleRow={saveSingleRow}
            headers={tableHeaders}
            changedFordeling={edit}
            id={studentId}
            publisertTilFs={publisertTilFsState}
            feilVedPubliseringOnPraksissted={feilVedPubliseringOnPraksissted}
          />
        );
      }),
    [
      feilmeldingerPerPraksissted,
      filteredAndSortedFordelinger,
      fordelingEdit,
      data.muligePraksissteder,
      data.muligeUndervisere,
      getPraksisstedItems,
      publisertTilFsState,
      saveSingleRow,
      tableHeaders,
      updateFordelingEdit,
    ],
  );

  const compare = Intl.Collator(locale || 'nb').compare;
  const sortStudentsByActiveCol = useCallback(
    (col: number, ascending: boolean) => {
      setSortedFordelinger(
        Object.entries(fordelingEdit).sort(
          ([studentIdA, fordelingA], [studentIdB, fordelingB]) => {
            const a = fordelingEdit[studentIdA];
            const b = fordelingEdit[studentIdB];

            const getPraksisplass = (studentRow: FordelingRow) =>
              muligePraksisplasser.find(
                (sted) => sted.praksissted.id === studentRow.praksissted.id,
              )?.praksissted.navn;

            const praksisplassA = getPraksisplass(a);
            const praksisplassB = getPraksisplass(b);

            if (ascending) {
              switch (col) {
                case 0:
                  return compareStudents(b.student, a.student, compare);
                case 2:
                  return compare(praksisplassB || '0', praksisplassA || '0');
              }
            } else {
              switch (col) {
                case 0:
                  return compareStudents(a.student, b.student, compare);
                case 2:
                  return compare(praksisplassA || '0', praksisplassB || '0');
              }
            }
            return 0;
          },
        ),
      );
    },
    [compare, fordelingEdit, muligePraksisplasser],
  );

  const headers = useMemo(() => {
    const canSortIndices = [0, 2];
    const canSearchIndices = [0, 1, 2, 3];

    return tableHeaders.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortStudentsByActiveCol}
        canSort={canSortIndices.includes(index)}
        search={(col, value) =>
          setSearch((prevState) =>
            prevState.map((searchValue, i) =>
              i === col ? value.toLowerCase() : searchValue,
            ),
          )
        }
        canSearch={canSearchIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));
  }, [tableHeaders, sortStudentsByActiveCol, activeCol]);

  useUnsavedChangesPrompt(hasUnsavedChanges);

  const [tableScrollTop, setTableScrollTop] = useState(
    getTableScrollTopFromSessionStorage(aktivitetsId),
  );

  const initScrollTopFromSessionStorage = useCallback(
    () => getTableScrollTopFromSessionStorage(aktivitetsId),
    [aktivitetsId],
  );

  return (
    <>
      {hasFeilmeldingerFraFsToPresent && (
        <>
          <FeilmeldingerFraFS
            feilmeldingerPerPraksissted={feilmeldingerPerPraksissted}
          />
          <PanelHeader title={labels.studentfordelingerHeader} />
        </>
      )}
      <Table
        className={css.fordelingTable}
        getInitTableWrapperScroll={initScrollTopFromSessionStorage}
        tableWrapperOnScroll={(scrollTop) => {
          putTableScrollTopInSessionStorage(aktivitetsId, scrollTop);
          setTableScrollTop(scrollTop);
        }}
        numHeaders={headers.length}
        tableMainScrollTop={tableScrollTop}
        blankOverflow
      >
        <TableHeader>
          <TableRow top>{headers}</TableRow>
        </TableHeader>
        <TableMain>{fordelingRows}</TableMain>
      </Table>
      <div className={css.buttonContainer}>
        <div className={css.assignmentContainer}>
          <span className={css.numUnassigned}>
            <NumberInput
              htmlId="numUnassignedStudents"
              value={numStudentsWithUnsavedPraksisplass}
              onChange={() => {}}
              disabled
              hideBorder
              ariaLabel={labels.uplasserte}
            />
          </span>
          <p className={css.unassigned}>{labels.uplasserte}</p>
          {canWrite && (
            <Button.Secondary
              id="assignButton"
              disabled={
                numStudentsWithUnsavedPraksisplass === 0 || capacityIsReached
              }
              onClick={autoAssign}
            >
              {labels.fordel}
            </Button.Secondary>
          )}
        </div>
        <FordelingDownloadButtons
          isAdmin={isAdmin}
          brukerId={bruker?.id || ''}
          aktivitetId={praksisaktivitet.id}
          isStudiestedbruker
        />
        {!publisertTilPsState && canWrite && (
          <Button.Secondary
            disabled={!publisertTilFsState}
            onClick={() => {
              setSharedwithPSConfirmation(true);
            }}
          >
            {labels.publiserTilPsa}
          </Button.Secondary>
        )}
        {canWrite && (
          <Button.Secondary
            disabled={
              publisertTilFsState ? !hasUnsavedChanges : hasUnsavedChanges
            }
            onClick={() => {
              if (!publisertTilFsState) {
                setResultConfirmation(true);
              } else {
                saveChangedRows(false, true);
              }
            }}
          >
            {!publisertTilFsState
              ? labels.publiserTilStudenter
              : labels.lagreEndringer}
          </Button.Secondary>
        )}
        {!publisertTilFsState && canWrite && (
          <Button.Secondary
            id="saveButton"
            disabled={!hasUnsavedChanges}
            onClick={() => {
              saveChangedRows(false, false);
            }}
          >
            {labels.lagre}
          </Button.Secondary>
        )}
        <ConfirmationDialog
          isOpen={sendResultConfirmation}
          onClose={sendResultConfirmationCloseHandler}
          title={labels.sendResultToFsDialogTitle}
        >
          <p>{labels.sendResultToFsDialogContent}</p>
        </ConfirmationDialog>
        <ConfirmationDialog
          isOpen={sharedWithPSConfirmation}
          onClose={shareResultConfirmationCloseHandler}
          title={labels.shareResultWithPsDialogTitle}
        >
          <p>{labels.shareResultWithPsDialogContent}</p>
        </ConfirmationDialog>
      </div>
      <div className={css.localAlert}>
        <LocalAlert
          open={visLocalAlert}
          type={AlertType.WHITE}
          handleClose={() => setVisLocalAlert(false)}
          contents={
            !publisertTilFsState
              ? labels.notSharedLocalAlertContent
              : !publisertTilPsState
              ? labels.sharedWithStudenterLocalAlertContent
              : labels.sharedWithStudenterAndPsLocalAlertContent
          }
        />
      </div>
      {hasUnsavedEmptyPraksisplasser && <p>{labels.autoFordelingSubtext}</p>}
    </>
  );
};

interface AlertStates {
  alertRowBackground: boolean;
  publisertFsAndNoUndervisere: boolean;
  publisertFsAndNoPraksissted: boolean;
  hasValideringsfeil: boolean;
  feilVedPubliseringOnPraksissted: boolean;
}

const getAlertStates = (
  fordeling: FordelingRow,
  publisertFs: boolean,
  feilVedPubliseringOnPraksissted: boolean,
): AlertStates => {
  const {
    hasValideringsfeil,
    undervisere: fordelingUndervisere,
    praksissted: fordelingPraksissted,
  } = fordeling;

  if (!publisertFs) {
    return {
      alertRowBackground: hasValideringsfeil,
      publisertFsAndNoPraksissted: false,
      publisertFsAndNoUndervisere: false,
      feilVedPubliseringOnPraksissted: false,
      hasValideringsfeil,
    };
  }

  const publisertFsAndNoUndervisere = fordelingUndervisere.ids.length === 0;
  const publisertFsAndNoPraksissted =
    !fordelingPraksissted ||
    fordelingPraksissted.id === notSelected ||
    fordelingPraksissted.id === '';

  const alertRowBackground =
    hasValideringsfeil ||
    publisertFsAndNoUndervisere ||
    publisertFsAndNoPraksissted ||
    feilVedPubliseringOnPraksissted;

  return {
    alertRowBackground,
    publisertFsAndNoUndervisere,
    publisertFsAndNoPraksissted,
    hasValideringsfeil,
    feilVedPubliseringOnPraksissted,
  };
};

const useGetAlertMessage = ({
  publisertFsAndNoPraksissted,
  publisertFsAndNoUndervisere,
  hasValideringsfeil,
  feilVedPubliseringOnPraksissted,
}: AlertStates): string | undefined => {
  const labels = useFormatMessageFor(translations);
  let message = hasValideringsfeil ? labels.avtalefeilUH : '';

  message = message.concat(
    feilVedPubliseringOnPraksissted
      ? labels.feilVedPubliseringOnPraksissted
      : '',
    publisertFsAndNoUndervisere ? labels.manglerUnderviser : '',
    publisertFsAndNoPraksissted ? labels.manglerPraksisplass : '',
  );

  return message.trim().length > 0 ? message : undefined;
};

interface FordelingTableRowProps {
  fordeling: FordelingRow;
  muligePraksissteder: PraksisstedTilFordelingDto[];
  praksisstedItems: DropdownOption[];
  muligeUndervisere: PersonDto[];
  updateFordelingEdit: (
    fordeling: FordelingRow,
    underviserIds: string[],
    praksisstedId: string,
    veilederIds: string[],
    wasSaved: boolean,
  ) => void;
  praksisplassAutoAssigned: boolean;
  saveSingleRow: (studentId: string) => void;
  headers: { title: string }[];
  publisertTilFs: boolean;
  changedFordeling?: FordelingRow;
  id?: string;
  feilVedPubliseringOnPraksissted: boolean;
}

const FordelingTableRow = ({
  fordeling,
  muligePraksissteder,
  praksisstedItems,
  muligeUndervisere,
  updateFordelingEdit,
  praksisplassAutoAssigned,
  saveSingleRow,
  headers,
  publisertTilFs,
  changedFordeling,
  id,
  feilVedPubliseringOnPraksissted,
}: FordelingTableRowProps) => {
  const praksisplassHasChanged = useMemo(
    () =>
      praksisplassAutoAssigned ||
      (changedFordeling &&
        changedFordeling.praksissted.id !==
          changedFordeling.praksissted.savedId),
    [changedFordeling, praksisplassAutoAssigned],
  );

  const underviserHasChanged = useMemo(
    () =>
      changedFordeling &&
      changedFordeling.undervisere.ids !==
        changedFordeling.undervisere.savedIds,
    [changedFordeling],
  );

  const veilederHasChanged = useMemo(
    () =>
      changedFordeling &&
      changedFordeling.veiledere.ids !== changedFordeling.veiledere.savedIds,
    [changedFordeling],
  );

  const rowHasChanged = useMemo(
    () => praksisplassHasChanged || underviserHasChanged || veilederHasChanged,
    [praksisplassHasChanged, underviserHasChanged, veilederHasChanged],
  );

  const praksisstedId = useMemo(
    () =>
      changedFordeling && changedFordeling.praksissted
        ? changedFordeling.praksissted.id
        : fordeling.praksissted
        ? fordeling.praksissted.id
        : '',
    [changedFordeling, fordeling.praksissted],
  );
  const underviserIds = useMemo(
    () =>
      changedFordeling
        ? changedFordeling.undervisere.ids
        : fordeling.undervisere.ids,
    [changedFordeling, fordeling.undervisere],
  );

  const veilederIds = useMemo(
    () =>
      changedFordeling
        ? changedFordeling.veiledere.ids
        : fordeling.veiledere.ids,
    [changedFordeling, fordeling.veiledere],
  );

  const nothingIsSelected = useMemo(
    () =>
      praksisstedId === '' &&
      underviserIds.length === 0 &&
      veilederIds.length === 0,
    [praksisstedId, underviserIds, veilederIds],
  );

  const veiledere = useMemo<PersonDto[]>(() => {
    const praksisplass = muligePraksissteder
      .flatMap((muligSted) => muligSted.muligePraksisplasser)
      .find((mp) => mp.praksissted.id === praksisstedId);

    return praksisplass ? praksisplass.muligeVeiledere : [];
  }, [muligePraksissteder, praksisstedId]);

  const onPraksisstedChanged = (psId: string) =>
    updateFordelingEdit(fordeling, underviserIds, psId, veilederIds, false);

  const onUnderviserChanged = (ids: string[]) =>
    updateFordelingEdit(fordeling, ids, praksisstedId, veilederIds, false);

  const onVeilederChanged = (ids: string[]) =>
    updateFordelingEdit(fordeling, underviserIds, praksisstedId, ids, false);

  const labels = useFormatMessageFor(translations);

  const underviserNames = useMemo(
    () =>
      muligeUndervisere
        .filter((underviser) => underviserIds.includes(underviser.id))
        .map((underviser) => ({
          id: underviser.id,
          navn: getFullName(underviser),
          checked: true,
        })),
    [muligeUndervisere, underviserIds],
  );

  const veilederNames = useMemo(
    () =>
      veiledere
        .filter((veileder) => veilederIds.includes(veileder.id))
        .map((veileder) => ({
          id: veileder.id,
          navn: getFullName(veileder),
          checked: true,
        })),
    [veiledere, veilederIds],
  );

  const { uhaUser: bruker } = useContext(UhaUserContext);
  const canWrite = useMemo(
    () => bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet,
    [bruker],
  );

  const isStudentActive = useMemo(
    () => fordeling.student.fsId != null,
    [fordeling.student],
  );

  const underviserHideBorderAndBackground = useMemo(
    () =>
      !underviserHasChanged &&
      fordeling.undervisere.ids.length ===
        fordeling.undervisere.savedIds.length &&
      underviserNames.length > 0,
    [underviserHasChanged, fordeling.undervisere, underviserNames],
  );

  const valgtPraksisplass = useMemo(
    () => praksisstedItems.find((option) => option.id === praksisstedId),
    [praksisstedItems, praksisstedId],
  );

  const praksisplassHideBorderAndBackground = useMemo(
    () =>
      !praksisplassHasChanged &&
      fordeling.praksissted.id === fordeling.praksissted.savedId &&
      valgtPraksisplass !== undefined,
    [praksisplassHasChanged, fordeling.praksissted, valgtPraksisplass],
  );

  const veilederHideBorderAndBackground = useMemo(
    () =>
      !veilederHasChanged &&
      fordeling.veiledere.ids.length === fordeling.veiledere.savedIds.length &&
      veilederNames.length > 0,
    [veilederHasChanged, fordeling.veiledere, veilederNames],
  );

  const valgtPraksisplassNoEdit = useMemo(() => {
    if (!canWrite || !isStudentActive) {
      return muligePraksissteder
        .find((mp) =>
          mp.muligePraksisplasser
            .map((pp) => pp.praksissted.id)
            .includes(praksisstedId),
        )
        ?.muligePraksisplasser.find((e) => e.praksissted.id === praksisstedId);
    }
  }, [canWrite, isStudentActive, muligePraksissteder, praksisstedId]);

  let tabHeader = 0;

  const studentnavn = getFullName(fordeling.student);

  const valgtPraksissted =
    valgtPraksisplass || valgtPraksisplassNoEdit?.praksissted;

  const alertStates = getAlertStates(
    fordeling,
    publisertTilFs,
    feilVedPubliseringOnPraksissted,
  );
  const alertMessage = useGetAlertMessage(alertStates);

  return (
    <TableRow
      rowBackground={
        alertStates.alertRowBackground
          ? 'alert'
          : praksisplassAutoAssigned
          ? 'shade'
          : undefined
      }
      className={nothingIsSelected ? css.nothingSelected : ''}
      id={id}
      title={alertMessage}
    >
      <TableCellTd header={headers[tabHeader++].title}>
        {isStudentActive ? (
          <NavLink
            to={'/myinstitution/studentbrukerprofil/' + fordeling.student.fsId}
          >
            {studentnavn}
          </NavLink>
        ) : (
          studentnavn
        )}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {canWrite && isStudentActive ? (
          <div className={css.selectMaxWidth}>
            <Dropdown
              id={`select-underviser-${fordeling.student.id}`}
              ariaLabel={'tutor-select-dropdown'}
              onChange={(event, value) => {
                onUnderviserChanged(
                  value.map((underviser: PersonDto) => underviser.id),
                );
              }}
              selectedOptions={underviserNames}
              options={[
                ...muligeUndervisere.map((underviser) => ({
                  id: underviser.id,
                  navn: getFullName(underviser),
                  checked: underviserIds.includes(underviser.id),
                })),
              ]}
              placeholder={labels.velgUnderviser}
              hideBorder={underviserHideBorderAndBackground}
              hideBackground={underviserHideBorderAndBackground}
              separator={';'}
              highlightPlaceholder
              multiple
            />
          </div>
        ) : (
          <div>
            {underviserNames.map((underviser) => underviser.navn).toString()}
          </div>
        )}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        <div
          className={classNames(
            css.praksisstedWrapper,
            (!canWrite || !isStudentActive) && css.setValue,
          )}
        >
          {canWrite && isStudentActive ? (
            <Dropdown
              id={`select-plassering-${fordeling.student.id}`}
              ariaLabel={'placement-select-dropdown'}
              onChange={(_, value) => onPraksisstedChanged(value.id)}
              placeholder={labels.velgPraksissted}
              selectedOption={valgtPraksisplass || { id: '', navn: '' }}
              options={[
                ...praksisstedItems,
                {
                  id: notSelected,
                  navn: labels.fjern,
                },
              ]}
              hideBackground={praksisplassHideBorderAndBackground}
              hideBorder={praksisplassHideBorderAndBackground}
              highlightPlaceholder
            />
          ) : (
            valgtPraksisplassNoEdit?.praksissted?.navn || ''
          )}
          {valgtPraksissted && (
            <div className={css.valueHoverCard}>
              <OnStedHoverCard
                sted={StedType.PRAKSISSTED}
                id={valgtPraksissted.id}
                navn={valgtPraksissted.navn}
              />
            </div>
          )}
        </div>
      </TableCellTd>
      <TableCellTd header={headers[tabHeader++].title}>
        {canWrite && isStudentActive ? (
          <div className={css.selectMaxWidth}>
            <Dropdown
              id={`select-veileder-${fordeling.student.id}`}
              ariaLabel={'supervisor-select-dropdown'}
              onChange={(event, value) => {
                onVeilederChanged(
                  value.map((veileder: PersonDto) => veileder.id),
                );
              }}
              selectedOptions={veilederNames}
              options={[
                ...veiledere.map((veileder) => ({
                  id: veileder.id,
                  navn: getFullName(veileder),
                  checked: veilederIds.includes(veileder.id),
                })),
              ]}
              placeholder={labels.velgVeileder}
              hideBorder={veilederHideBorderAndBackground}
              hideBackground={veilederHideBorderAndBackground}
              noOptionsText={labels.noVeilederOptionsUha}
              separator={';'}
              highlightPlaceholder
              multiple
            />
          </div>
        ) : (
          <div>{veilederNames.map((veileder) => veileder.navn).toString()}</div>
        )}
      </TableCellTd>
      <TableCellTd header={headers[tabHeader].title}>
        {isStudentActive && (
          <RowStatusIcon
            rowHasChanged={rowHasChanged || false}
            nothingIsSelected={nothingIsSelected}
            saveRow={() => {
              saveSingleRow(fordeling.student.id);
            }}
          />
        )}
      </TableCellTd>
    </TableRow>
  );
};

interface RowStatusIconProps {
  rowHasChanged: boolean;
  nothingIsSelected: boolean;
  saveRow: () => void;
}
const RowStatusIcon = React.memo<RowStatusIconProps>(
  ({ rowHasChanged, nothingIsSelected, saveRow }) => {
    const { formatMessage } = useIntl();
    const labels = useMemo(
      () => ({
        infoRowChanged: formatMessage({ id: translations.infoRowChanged.id }),
        infoRowEmpty: formatMessage({ id: translations.infoRowEmpty.id }),
        infoRowSaved: formatMessage({ id: translations.infoRowSaved.id }),
      }),
      [formatMessage],
    );

    if (rowHasChanged) {
      return (
        <IconButton className={css.rowButton} onClick={saveRow} size="large">
          <Icon
            type={IconType.CheckCircleOutline}
            className={css.rowStatusIcon}
            title={labels.infoRowChanged}
          />
        </IconButton>
      );
    }

    if (nothingIsSelected) {
      return (
        <Icon
          type={IconType.RadioButtonUnchecked}
          className={css.rowStatusIcon}
          title={labels.infoRowEmpty}
        />
      );
    }

    return (
      <Icon
        type={IconType.CheckCircle}
        className={css.rowStatusIcon}
        title={labels.infoRowSaved}
      />
    );
  },
);

export default Fordeling;
